import { DetailedInsight } from '@api/api/insights';
import { KgInsight } from '@api/api/kg-insights';
import { Markdown } from '@terminal/components/markdown/Markdown';
import { PosthogTrackingSource } from '@terminal/services/tracking/posthog/constants';
import { PulseCompanyCell } from '@terminal/views/theme-overview/tab-content/pulse-overview/components/pulse-company-cell/PulseCompanyCell';
import { usePulseCitationClick } from '@terminal/views/theme-overview/tab-content/pulse-overview/hooks/usePulseCitationClick';
import {
  PulseMonthlyDriver,
  PulseMonthlySummary,
  PulseSentimentDirection,
} from '@terminal/views/theme-overview/tab-content/pulse-overview/pulse-types';
import { linkDailySummaryCitations } from '@terminal/views/theme-overview/tab-content/pulse-overview/utils/linkDailySummaryCitations';
import { clsx } from 'clsx';
import { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './SentimentSummaryCard.module.scss';

export const SentimentSummaryCard = ({
  direction,
  summary,
  themeId,
  insightsById,
  scenarioInsightIds,
  source,
  onOpen,
}: {
  direction: PulseSentimentDirection;
  summary: PulseMonthlySummary;
  themeId?: string;
  insightsById: Map<string, KgInsight | DetailedInsight>;
  scenarioInsightIds: ReadonlySet<string>;
  source: PosthogTrackingSource;
  onOpen: () => void;
}) => {
  const { t } = useTranslation(['widget']);
  const { handleCitationClick, openKgInsight } = usePulseCitationClick({
    themeId,
    insightsById,
    scenarioInsightIds,
    direction,
    source,
  });
  const hasDrivers = summary.drivenBy.length > 0;
  const isEmptyNarrative = summary.narrative.trim() === '';
  const summaryWithCitationLinks = linkDailySummaryCitations(
    summary.narrative,
    summary.citations
  );

  const handleCompanyClick = (
    event: MouseEvent,
    company: PulseMonthlyDriver
  ) => {
    event.stopPropagation();
    const insight = insightsById.get(company.insightId);
    // TODO: handle scenario insights
    if (!insight || 'metadata' in insight) {
      return;
    }

    openKgInsight(insight);
  };

  const handleCardClick = (event: MouseEvent<HTMLDivElement>) => {
    if (!handleCitationClick(event)) {
      onOpen();
    }
  };

  return (
    <div
      className={clsx(styles.card, styles[direction])}
      onClick={handleCardClick}
      data-testid={`pulse-sentiment-card-${direction}`}
    >
      <span className={styles.cardTitle}>
        {t(`widget:themeOverview.pulse.${direction}Sentiment`)}
      </span>
      <span
        className={clsx(styles.narrative, {
          [styles.narrativeWithoutDrivers]: !hasDrivers,
          [styles.emptyNarrative]: isEmptyNarrative,
        })}
      >
        {isEmptyNarrative ? (
          t(`widget:themeOverview.pulse.noSignalsInLast30Days_${direction}`)
        ) : (
          <Markdown message={summaryWithCitationLinks} />
        )}
      </span>
      {hasDrivers && (
        <>
          <span className={styles.drivenByLabel}>
            {t('widget:themeOverview.pulse.drivenByRecency')}
          </span>
          <span className={styles.companies}>
            {summary.drivenBy.map(company => (
              <span
                key={company.entityTag}
                className={styles.companyRow}
                onClick={event => handleCompanyClick(event, company)}
              >
                <PulseCompanyCell company={company} layout="inline" />
              </span>
            ))}
          </span>
        </>
      )}
    </div>
  );
};
